import * as React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout';
import { ToastProvider } from '@/components/ui/Toast';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { useCookbookStore } from '@/stores/cookbook-store';
import { useLanguageStore } from '@/stores/language-store';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { initDatabase } from '@/lib/db';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { startSync, stopSync } from '@/lib/sync';
import { migrateLocalToCloud } from '@/lib/migration';

// ---------------------------------------------------------------------------
// Splash / Loading screens
// ---------------------------------------------------------------------------

function SplashScreen() {
  const { t } = useTranslation('common');
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F9FAFB',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <img
          src="/logo.png"
          alt={t('app.name')}
          style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '16px',
            objectFit: 'contain',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
        <p style={{ color: '#6B7280' }}>{t('loading.app')}</p>
      </div>
    </div>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  const { t } = useTranslation('common');
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F9FAFB',
      }}
    >
      <div style={{ textAlign: 'center', padding: '24px', maxWidth: '400px' }}>
        <div
          style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ color: 'white', fontWeight: 700, fontSize: '24px' }}>!</span>
        </div>
        <h1 style={{ color: '#111827', fontSize: '20px', fontWeight: 700, margin: '0 0 8px 0' }}>
          {t('error.failedToLoad')}
        </h1>
        <p style={{ color: '#6B7280', marginBottom: '16px' }}>
          {error.message || t('error.somethingWentWrong')}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('btn.retry')}
        </button>
      </div>
    </div>
  );
}

function PageLoader() {
  const { t } = useTranslation('common');
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            margin: '0 auto 8px',
            borderRadius: '8px',
            background: '#CFFAFE',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
        <p style={{ fontSize: '14px', color: '#6B7280' }}>{t('loading.page')}</p>
      </div>
    </div>
  );
}

function NotFoundPage() {
  const { t } = useTranslation('common');
  return (
    <div style={{ textAlign: 'center', padding: '48px' }}>
      <h1 style={{ fontSize: '48px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>{t('error.404')}</h1>
      <p style={{ color: '#6B7280', marginBottom: '16px' }}>{t('error.pageNotFound')}</p>
      <a
        href="/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
          color: 'white',
          borderRadius: '10px',
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        {t('btn.goHome')}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuthBootstrap — Firebase auth listener + language sync + wait for auth
// ---------------------------------------------------------------------------

function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const { _setUser, _setAuthReady, isAuthReady } = useAuthStore();
  const { language, direction } = useLanguageStore();

  // Sync language and direction attributes on the document
  React.useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
  }, [language, direction]);

  // Set up Firebase auth listener
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      _setUser(firebaseUser);
      _setAuthReady();
    });
    return unsubscribe;
  }, [_setUser, _setAuthReady]);

  if (!isAuthReady) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// RequireAuth — redirects to /auth if not signed in
// ---------------------------------------------------------------------------

function RequireAuth() {
  const user = useAuthStore((s) => s.user);

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
}

// ---------------------------------------------------------------------------
// DataInitializer — migration + sync + data loading (only runs when authed)
// ---------------------------------------------------------------------------

function DataInitializer() {
  const { loadRecipes } = useCookbookStore();
  const loadSettings = useUIStore((s) => s.loadSettings);
  const user = useAuthStore((s) => s.user);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [initError, setInitError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!user) return;

    let cancelled = false;
    setIsInitialized(false);
    setInitError(null);

    const init = async () => {
      try {
        await initDatabase();

        if (cancelled) return;

        // Migrate local data to cloud first (no-op if user already has cloud data)
        try {
          const result = await migrateLocalToCloud(user.uid);
          if (!result.skipped && result.total > 0) {
            console.log(`[Migration] Uploaded ${result.total} items to cloud`);
          }
        } catch (err) {
          console.error('[Migration] Failed:', err);
        }

        if (cancelled) return;

        // Start real-time sync after migration completes
        startSync(user.uid);

        if (cancelled) return;

        // Load recipes and app settings in parallel. loadSettings reads
        // soundEnabled/vibrationEnabled/keepScreenAwake/etc. from IndexedDB or
        // Firestore so the UI starts with the user's actual preferences instead
        // of DEFAULT_SETTINGS on every page load.
        await Promise.all([loadRecipes(), loadSettings()]);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        if (!cancelled) {
          setInitError(error instanceof Error ? error : new Error('Unknown initialization error'));
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      stopSync();
    };
  }, [user, loadRecipes, loadSettings]);

  if (initError) {
    return <ErrorScreen error={initError} />;
  }

  if (!isInitialized) {
    return <SplashScreen />;
  }

  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Lazy page imports
// ---------------------------------------------------------------------------

const LazyHomePage = React.lazy(() =>
  import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
);
const LazyDiscoverPage = React.lazy(() =>
  import('@/pages/DiscoverPage').then((m) => ({ default: m.DiscoverPage }))
);
const LazyCookbookPage = React.lazy(() =>
  import('@/pages/CookbookPage').then((m) => ({ default: m.CookbookPage }))
);
const LazyMealPlannerPage = React.lazy(() =>
  import('@/pages/MealPlannerPage').then((m) => ({ default: m.MealPlannerPage }))
);
const LazyPantryPage = React.lazy(() =>
  import('@/pages/PantryPage').then((m) => ({ default: m.PantryPage }))
);
const LazyRecipeDetailPage = React.lazy(() =>
  import('@/pages/RecipeDetailPage').then((m) => ({ default: m.RecipeDetailPage }))
);
const LazyRecipeFormPage = React.lazy(() =>
  import('@/pages/RecipeFormPage').then((m) => ({ default: m.RecipeFormPage }))
);
const LazyCookEntryPage = React.lazy(() =>
  import('@/pages/CookEntryPage').then((m) => ({ default: m.CookEntryPage }))
);
const LazyCookingSessionPage = React.lazy(() =>
  import('@/pages/CookingSessionPage').then((m) => ({ default: m.CookingSessionPage }))
);
const LazySettingsPage = React.lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const LazyAuthPage = React.lazy(() =>
  import('@/pages/AuthPage').then((m) => ({ default: m.AuthPage }))
);

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <AuthBootstrap>
            <Routes>
              {/* Auth page — accessible without sign-in */}
              <Route
                path="auth"
                element={
                  <React.Suspense fallback={<PageLoader />}>
                    <LazyAuthPage />
                  </React.Suspense>
                }
              />

              {/* All other routes require auth + data initialization */}
              <Route element={<RequireAuth />}>
                <Route element={<DataInitializer />}>
                  {/* Main app shell routes */}
                  <Route element={<AppShell />}>
                    <Route
                      index
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyHomePage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="discover"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyDiscoverPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="planner"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyMealPlannerPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="pantry"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyPantryPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="cookbook"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyCookbookPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="cookbook/new"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyRecipeFormPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="cookbook/:id/edit"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyRecipeFormPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="cookbook/:id"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyRecipeDetailPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="cook"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazyCookEntryPage />
                        </React.Suspense>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <React.Suspense fallback={<PageLoader />}>
                          <LazySettingsPage />
                        </React.Suspense>
                      }
                    />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  {/* Cooking session — outside app shell but still requires auth */}
                  <Route
                    path="cook/session"
                    element={
                      <React.Suspense fallback={<PageLoader />}>
                        <LazyCookingSessionPage />
                      </React.Suspense>
                    }
                  />
                </Route>
              </Route>
            </Routes>
          </AuthBootstrap>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
